import {FC, ReactNode} from "react";
import {Navigate} from "react-router";
import {isRoleAtLeast} from "utils/roles";
import {Role} from "types/global";
import {useAuthenticatedUser} from "../user";

/**
 * RoleGuard Component
 *
 * A higher-order component that restricts access to its children based on
 * the authenticated user's role(s).
 *
 * Role Hierarchy
 * Roles are ordered hierarchically (e.g. `USER < MODERATOR < MANAGER < ADMIN < SUPER_ADMIN`).
 * Access checks are performed using {@link isRoleAtLeast}, which returns true if
 * the user's role is greater than or equal to the required role.
 *
 * Logic
 * - If `minRole` is a `"MANAGER"`, then:
 *   - Users with `"MANAGER"`, `"ADMIN"`, or `"SUPER_ADMIN"` are allowed.
 *   - Users with `"USER"` or `"MODERATOR"` are denied.
 * Auth Flow
 * 1. While the authentication state is loading -> show a loading screen.
 * 2. If no user is authenticated -> redirect to `/login`.
 * 3. If the user's role satisfies the requirement(s) -> render children.
 * 4. Otherwise -> redirect to `/forbidden`.
 *
 * @param children - The JSX content to render if the access check passes.
 * @param minRole - A single role interpreted as a minimum required role in the hierarchy.
 *
 * @example
 * // Only SUPER_ADMINs can access
 * <RoleGuard minRole="SUPER_ADMIN">
 *   <AdminDashboard />
 * </RoleGuard>
 */
type RoleGuardProps = {
  children: ReactNode;
  minRole: Role;
};

const RoleGuard: FC<RoleGuardProps> = ({children, minRole}) => {
  const user = useAuthenticatedUser();

  if (!user) return <Navigate to="/login" replace />;

  if (!isRoleAtLeast(user.role, minRole))
    return <Navigate to="/forbidden" replace />;

  return <>{children}</>;
};

export default RoleGuard;
